import { useCallback, useRef } from 'react';
import { startTransition } from 'react';
import { executeBusinessLoad } from '../../../domain/persistence/businessLoadOrchestrator';
import { normalizeBusinessStorageId } from '../../../utils/unifiedDataHelpers';

/**
 * Full KV → React business load (Strangler extraction from DataContext).
 * Coalesces concurrent loads per business so writes never storm a full boot.
 */
export function useBusinessLoad({ setters, setDataLoaded, setDataShellReady, setReady }) {
  const settersRef = useRef(setters);
  settersRef.current = setters;
  const setDataLoadedRef = useRef(setDataLoaded);
  setDataLoadedRef.current = setDataLoaded;
  const setDataShellReadyRef = useRef(setDataShellReady);
  setDataShellReadyRef.current = setDataShellReady;
  const setReadyRef = useRef(setReady);
  setReadyRef.current = setReady;
  /** @type {React.MutableRefObject<Map<string, Promise<void>>>} */
  const inflightRef = useRef(new Map());

  const loadBusinessData = useCallback(async (businessId) => {
    const key = String(normalizeBusinessStorageId(businessId) || businessId || '');
    if (!key) return;

    const existing = inflightRef.current.get(key);
    if (existing) return existing;

    const promise = executeBusinessLoad(businessId, {
      setters: settersRef.current,
      setDataLoaded: (value) => setDataLoadedRef.current(value),
      setDataShellReady: setDataShellReadyRef.current
        ? (value) => setDataShellReadyRef.current(value)
        : undefined,
      setReady: (value) => setReadyRef.current(value),
      startTransition
    }).finally(() => {
      if (inflightRef.current.get(key) === promise) {
        inflightRef.current.delete(key);
      }
    });

    inflightRef.current.set(key, promise);
    return promise;
  }, []);

  return { loadBusinessData };
}
